"use client";

import { type FC, type ReactNode, useEffect, useState } from "react";

import { cn } from "../../utils";
import { NavigationIcon } from "./components";

export interface CarouselProps {
  children: ReactNode[];
  className?: string;
  controlsClassName?: string;
  value?: number;
  infinite?: boolean;
  showControls?: boolean;
  onChange?: (index: number) => void;
}

export const Carousel: FC<CarouselProps> = ({
  children,
  className,
  controlsClassName,
  value = 0,
  infinite = false,
  showControls = true,
  onChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState(value);
  const totalSlides = children.length;
  const lastSlideIndex = totalSlides - 1;

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => {
      if (infinite) {
        return prevIndex === 0 ? lastSlideIndex : prevIndex - 1;
      }

      return Math.max(0, prevIndex - 1);
    });
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => {
      if (infinite) {
        return prevIndex === lastSlideIndex ? 0 : prevIndex + 1;
      }

      return Math.min(lastSlideIndex, prevIndex + 1);
    });
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    onChange?.(currentIndex);
  }, [currentIndex, onChange]);

  useEffect(() => {
    setCurrentIndex(value);
  }, [value]);

  return (
    <div className={cn("ui:relative ui:w-full", className)}>
      <div className="ui:relative ui:overflow-hidden">
        {children.map((child, index) => (
          <div
            key={index}
            className={cn(
              "ui:absolute ui:inset-0 ui:transition-opacity ui:duration-500 ui:ease-in-out",
              index === currentIndex ? "ui:opacity-100" : "ui:opacity-0",
            )}
          >
            {child}
          </div>
        ))}
        {/* Invisible first child to establish height */}
        <div className="ui:invisible">{children[0]}</div>
      </div>

      {showControls && (
        <div
          className={cn(
            "ui:mt-4 ui:flex ui:items-center ui:gap-4 ui:px-6",
            controlsClassName,
          )}
        >
          <NavigationIcon
            direction="previous"
            disabled={currentIndex === 0 && !infinite}
            onClick={goToPrevious}
          />

          <div className="ui:flex ui:w-full ui:gap-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                tabIndex={-1}
                disabled={index === currentIndex}
                aria-label={`Go to slide ${index + 1}`}
                className={cn(
                  "ui:h-2 ui:flex-1 ui:rounded-full ui:transition-colors ui:duration-500",
                  index === currentIndex
                    ? "ui:bg-primary"
                    : "ui:cursor-pointer ui:bg-divider ui:hover:bg-textPrimary/30",
                )}
                onClick={() => {
                  goToSlide(index);
                }}
              />
            ))}
          </div>

          <NavigationIcon
            direction="next"
            disabled={currentIndex === lastSlideIndex && !infinite}
            onClick={goToNext}
          />
        </div>
      )}
    </div>
  );
};
